/**
 * Lazy Load Image Helper
 * Uses IntersectionObserver to load images only when they're about to enter the viewport.
 *
 * Usage:
 * - Add data-lazy-src="image-url" to any <img> element, OR
 * - Add data-deferred-img attribute with data-src and optional data-srcset attributes
 *
 * The script will load the image when it's about to be visible or is already in view on page load.
 */
(function() {
  'use strict';

  function loadImage(img) {
    // Handle data-lazy-src pattern
    const lazySrc = img.dataset.lazySrc;
    if (lazySrc) {
      img.src = lazySrc;
      img.removeAttribute('data-lazy-src');
    }

    // Handle data-deferred-img pattern (from image.liquid deferred mode)
    if (img.hasAttribute('data-deferred-img')) {
      const deferredSrc = img.dataset.src;
      const deferredSrcset = img.dataset.srcset;

      if (deferredSrc) {
        img.src = deferredSrc;
        img.removeAttribute('data-src');
      }
      if (deferredSrcset) {
        img.srcset = deferredSrcset;
        img.removeAttribute('data-srcset');
      }
      img.removeAttribute('data-deferred-img');
    }

    img.classList.add('lazy-loaded');
  }

  if (!('IntersectionObserver' in window)) {
    // Fallback: load all images immediately if IntersectionObserver is not supported
    document.querySelectorAll('img[data-lazy-src], img[data-deferred-img]').forEach(function(img) {
      loadImage(img);
    });
    return;
  }

  const lazyLoadObserver = new IntersectionObserver(function(entries, observer) {
    entries.forEach(function(entry) {
      if (entry.isIntersecting) {
        const img = entry.target;
        loadImage(img);
        observer.unobserve(img);
      }
    });
  }, {
    rootMargin: '200px 0px', // Start loading 200px before entering viewport
    threshold: 0
  });

  function initLazyImages(container) {
    const selector = 'img[data-lazy-src]:not(.lazy-loaded), img[data-deferred-img]:not(.lazy-loaded)';
    const images = container ? container.querySelectorAll(selector) : document.querySelectorAll(selector);

    images.forEach(function(img) {
      lazyLoadObserver.observe(img);
    });
  }

  // Initialize on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initLazyImages();
    });
  } else {
    initLazyImages();
  }

  // Watch for dynamically added images
  if ('MutationObserver' in window) {
    const mutationObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            if (node.tagName === 'IMG' && (node.dataset.lazySrc || node.hasAttribute('data-deferred-img'))) {
              lazyLoadObserver.observe(node);
            }
            // Check descendants
            if (node.querySelectorAll) {
              initLazyImages(node);
            }
          }
        });
      });
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Expose globally for manual initialization if needed
  window.lazyLoadImages = initLazyImages;
})();
